import { useEffect, useRef, useState } from 'react';

import GetPaginatedResponse from '../../Api/CRM/Responses/PaginatedResponse';
import { SortOrder, SortParams } from '../elements/common/SortButton';
import { ColumnFilters, FilterChangeCallback, FilterCommand } from '../elements/common/types';

import useIsMounted from './useIsMounted';

export type FetchPaginatorParams = {
    filter: string;
    sort: string;
    columnFilters: ColumnFilters;
};

type FetchPaginatorCallback<T> = (page: number, params: FetchPaginatorParams) => Promise<GetPaginatedResponse<T>>;

export type UpdateItemsFunction = (page?: number) => Promise<void>;

function useAutoPaginator<T>(fetchPaginator: FetchPaginatorCallback<T>) {
    const isMounted = useIsMounted();
    const [items, setItems] = useState<T[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const currentPage = useRef<number>(1);
    const lastPage = useRef<number>(1);
    const filter = useRef<string>('');
    const [columnFilters, setColumnFilters] = useState<ColumnFilters>({});
    const sortParams = useRef<SortParams>({});

    async function updateItems(page = currentPage.current) {
        try {
            setIsLoading(true);
            const paginator = await fetchPaginator(page, {
                filter: filter.current,
                sort: formatSortParams(sortParams.current),
                columnFilters,
            });

            if (!(paginator && Array.isArray(paginator.data) && paginator.last_page >= 1)) {
                throw new Error('The fetched data is not a paginator!');
            }

            if (isMounted()) {
                currentPage.current = paginator.current_page;
                lastPage.current = paginator.last_page;
                setItems(paginator.data);
            }

            if (paginator.current_page > paginator.last_page) {
                updateItems(paginator.last_page); // last item from last page was deleted
            }
        } finally {
            if (isMounted()) {
                setIsLoading(false);
            }
        }
    }

    const debouncedUpdateItems = useCustomDebounce(updateItems);

    const handleSearchFilterChange = (value: string) => {
        filter.current = value;
        updateItems(1);
    };

    const handleSortParamsChange = (column: string, order: SortOrder) => {
        if (order !== SortOrder.OFF) {
            sortParams.current[column] = order;
        } else {
            delete sortParams.current[column];
        }
        debouncedUpdateItems();
    };

    const handleColumnFiltersChange: FilterChangeCallback = (column: string, command: FilterCommand) => {
        setColumnFilters({ ...columnFilters, [column]: command });
    };

    useEffect(() => {
        debouncedUpdateItems();
    }, [columnFilters]);

    return {
        items,
        setItems,
        updateItems,
        isLoading,
        setIsLoading,
        auto: {
            paginator: { currentPage: currentPage.current, lastPage: lastPage.current, onPageChange: updateItems },
            search: { filterTable: handleSearchFilterChange },
            sort: { sortParams: sortParams.current, onSortChange: handleSortParamsChange },
            filter: { columnFilters, onFilterChange: handleColumnFiltersChange },
        },
    };
}

function formatSortParams(sortParams: SortParams) {
    return Object.entries(sortParams)
        .map(([key, value]) => `${key}:${value}`)
        .join(',');
}

function useCustomDebounce(updateItems: Function) {
    const debounce = useRef<{ timeout?: NodeJS.Timeout; hasMadeRequest?: boolean }>({});

    return () => {
        clearTimeout(debounce.current.timeout);

        debounce.current.timeout = setTimeout(
            () => {
                updateItems();
                debounce.current.hasMadeRequest = true;
            },
            debounce.current.hasMadeRequest ? 800 : 100
        );
    };
}

export default useAutoPaginator;
